import { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Activity, Database, Globe, BarChart3, TrendingUp,
  FileText, Layers, Clock, CheckCircle2, AlertCircle, Zap
} from "lucide-react";

import { API } from "../../lib/constants";
import { Shimmer } from "../common/DashboardSkeleton";
import { usePrefetchOrFetch } from "../common/DataPrefetch";

const Reveal = ({ children, delay = 0 }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const t = setTimeout(() => setVisible(true), 50 + delay);
    return () => clearTimeout(t);
  }, [delay]);
  return (
    <div ref={ref} style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(12px)", transition: `all 0.5s ease ${delay}ms` }}>
      {children}
    </div>
  );
};

const sourceColors = {
  arxiv: { bg: "rgba(245, 158, 11, 0.15)", border: "rgba(245, 158, 11, 0.3)", text: "#f59e0b", label: "arXiv" },
  huggingface: { bg: "rgba(168, 85, 247, 0.15)", border: "rgba(168, 85, 247, 0.3)", text: "#a855f7", label: "HuggingFace" },
  semantic_scholar: { bg: "rgba(6, 182, 212, 0.15)", border: "rgba(6, 182, 212, 0.3)", text: "#06b6d4", label: "Semantic Scholar" },
  ssrn: { bg: "rgba(34, 197, 94, 0.15)", border: "rgba(34, 197, 94, 0.3)", text: "#22c55e", label: "SSRN" },
  stanford_hai: { bg: "rgba(239, 68, 68, 0.15)", border: "rgba(239, 68, 68, 0.3)", text: "#ef4444", label: "Stanford HAI" },
};

const MiniBar = ({ value, max, color }) => (
  <div className="w-full h-1.5 rounded-full bg-white/5 overflow-hidden">
    <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${Math.min((value / max) * 100, 100)}%`, background: color }} />
  </div>
);

export const ResearchPulse = () => {
  const [data, loading] = usePrefetchOrFetch(
    "researchPulse",
    `${API}/research/pulse`,
    {
      validate: (d) => d && typeof d.total === "number" && d.sources && Object.keys(d.sources).length > 0,
      delay: 500, // snappy fallback if the store never delivers valid data
    }
  );

  if (loading) {
    return (
      <div className="space-y-5 animate-fade-in" data-testid="research-pulse-loading">
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5 space-y-3">
          <Shimmer className="h-3 w-36" />
          <Shimmer className="h-8 w-56" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 space-y-2">
              <Shimmer className="h-2.5 w-16" />
              <Shimmer className="h-7 w-20" />
              <Shimmer className="h-1.5 w-full rounded-full" />
            </div>
          ))}
        </div>
        <p className="text-center text-[10px] text-slate-500 animate-pulse">Loading research pulse data...</p>
      </div>
    );
  }

  if (!data) return <div className="text-slate-500 text-center py-10">Unable to load pulse data.</div>;

  const maxSource = Math.max(...Object.values(data.sources || {}));
  const maxCountry = data.countries?.[0]?.count || 1;
  const maxField = data.top_fields?.[0]?.count || 1;

  return (
    <div className="space-y-6" data-testid="research-pulse">
      {/* Header */}
      <Reveal>
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ background: "rgba(6,182,212,0.15)" }}>
            <Activity className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Research Pulse</h2>
            <p className="text-[10px] text-slate-500">Live data pipeline health, coverage metrics, and source diversity</p>
          </div>
        </div>
      </Reveal>

      {/* Top Stats Row */}
      <Reveal delay={80}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { icon: Database, label: "Total Papers", value: data.total?.toLocaleString(), color: "#06b6d4" },
            { icon: Layers, label: "Data Sources", value: Object.keys(data.sources || {}).length, color: "#a855f7" },
            { icon: Globe, label: "Countries", value: data.countries_total, color: "#22c55e" },
            { icon: CheckCircle2, label: "Classified", value: `${data.classification_rate}%`, color: data.classification_rate > 50 ? "#22c55e" : "#f59e0b" },
          ].map((s, i) => (
            <div key={s.label} className="p-3 rounded-lg border border-white/5 bg-white/[0.02]" data-testid={`pulse-stat-${i}`}>
              <div className="flex items-center gap-2 mb-1">
                <s.icon className="w-3.5 h-3.5" style={{ color: s.color }} />
                <span className="text-[9px] text-slate-500 uppercase tracking-wider">{s.label}</span>
              </div>
              <span className="text-xl font-bold text-white">{s.value}</span>
            </div>
          ))}
        </div>
      </Reveal>

      {/* Source Pipeline */}
      <Reveal delay={160}>
        <div className="p-4 rounded-lg border border-white/5 bg-white/[0.02]">
          <div className="flex items-center gap-2 mb-4">
            <Database className="w-4 h-4 text-cyan-400" />
            <h3 className="text-sm font-semibold text-white">Data Pipeline — Sources</h3>
          </div>
          <div className="space-y-3">
            {Object.entries(data.sources || {}).sort((a, b) => b[1] - a[1]).map(([src, count]) => {
              const s = sourceColors[src] || { bg: "rgba(100,100,100,0.15)", border: "rgba(100,100,100,0.3)", text: "#94a3b8", label: src };
              const fresh = data.freshness?.[src];
              return (
                <div key={src} className="flex items-center gap-3" data-testid={`pulse-source-${src}`}>
                  <div className="w-28 flex-shrink-0">
                    <span className="text-xs font-medium" style={{ color: s.text }}>{s.label}</span>
                  </div>
                  <div className="flex-1">
                    <MiniBar value={count} max={maxSource} color={s.text} />
                  </div>
                  <span className="text-xs text-slate-300 w-16 text-right font-mono">{count.toLocaleString()}</span>
                  {fresh && (
                    <span className="text-[9px] text-slate-500 w-24 text-right">{fresh.oldest}–{fresh.newest}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </Reveal>

      {/* Classification + Geo Coverage */}
      <Reveal delay={240}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Classification Progress */}
          <div className="p-4 rounded-lg border border-white/5 bg-white/[0.02]">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <h3 className="text-sm font-semibold text-white">Classification Progress</h3>
            </div>
            <div className="flex items-end gap-4 mb-3">
              <div>
                <span className="text-3xl font-bold text-emerald-400">{data.classification_rate}%</span>
                <p className="text-[9px] text-slate-500 mt-1">of papers classified by orientation</p>
              </div>
              <div className="flex-1 text-right">
                <p className="text-xs text-slate-400">{data.classified?.toLocaleString()} <span className="text-emerald-400">classified</span></p>
                <p className="text-xs text-slate-400">{data.unclassified?.toLocaleString()} <span className="text-amber-400">pending</span></p>
              </div>
            </div>
            <div className="w-full h-3 rounded-full bg-slate-800 overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 transition-all duration-1000"
                style={{ width: `${data.classification_rate}%` }} />
            </div>
            {/* Orientation breakdown */}
            <div className="mt-3 grid grid-cols-2 gap-2">
              {Object.entries(data.orientations || {}).filter(([k]) => k !== "unclassified").map(([orient, count]) => (
                <div key={orient} className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{
                    background: orient === "care" ? "#22c55e" : orient === "utility" ? "#3b82f6" : orient === "creative" ? "#a855f7" : "#ef4444"
                  }} />
                  <span className="text-[10px] text-slate-400 capitalize">{orient}</span>
                  <span className="text-[10px] text-slate-500 ml-auto">{count?.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Geographic Coverage */}
          <div className="p-4 rounded-lg border border-white/5 bg-white/[0.02]">
            <div className="flex items-center gap-2 mb-3">
              <Globe className="w-4 h-4 text-green-400" />
              <h3 className="text-sm font-semibold text-white">Geographic Coverage</h3>
            </div>
            <div className="flex items-end gap-4 mb-3">
              <div>
                <span className="text-3xl font-bold text-green-400">{data.geo_coverage_pct}%</span>
                <p className="text-[9px] text-slate-500 mt-1">papers with known country</p>
              </div>
              <div className="flex-1 text-right">
                <p className="text-xs text-slate-400">{data.countries_total} countries</p>
                <p className="text-xs text-slate-400">{data.geo_unknown?.toLocaleString()} <span className="text-amber-400">unknown</span></p>
              </div>
            </div>
            <div className="space-y-1.5 mt-2">
              {(data.countries || []).slice(0, 8).map((c, i) => (
                <div key={c.country || i} className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-400 w-24 truncate">{c.country}</span>
                  <div className="flex-1">
                    <MiniBar value={c.count} max={maxCountry} color="#22c55e" />
                  </div>
                  <span className="text-[10px] text-slate-500 w-10 text-right font-mono">{c.count.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Reveal>

      {/* Ingestion Timeline */}
      <Reveal delay={320}>
        <div className="p-4 rounded-lg border border-white/5 bg-white/[0.02]">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-indigo-400" />
            <h3 className="text-sm font-semibold text-white">Ingestion Timeline by Source</h3>
          </div>
          <div className="overflow-x-auto">
            <div className="flex items-end gap-1 min-w-[500px] h-40">
              {(data.ingestion_timeline || []).map((yr, i) => {
                const total = Object.entries(yr).filter(([k]) => k !== "year").reduce((s, [, v]) => s + v, 0);
                const maxTotal = Math.max(...(data.ingestion_timeline || []).map(y =>
                  Object.entries(y).filter(([k]) => k !== "year").reduce((s, [, v]) => s + v, 0)
                ));
                const heightPct = maxTotal > 0 ? (total / maxTotal) * 100 : 0;

                return (
                  <div key={yr.year || i} className="flex-1 flex flex-col items-center gap-1 group">
                    <div className="w-full flex flex-col justify-end" style={{ height: "130px" }}>
                      {Object.entries(yr).filter(([k]) => k !== "year").reverse().map(([src, count]) => {
                        const srcColor = sourceColors[src]?.text || "#94a3b8";
                        const segPct = total > 0 ? (count / total) * heightPct : 0;
                        return (
                          <div key={src} className="w-full rounded-sm transition-all duration-300 group-hover:opacity-80"
                            style={{ height: `${segPct}%`, background: srcColor, minHeight: count > 0 ? "2px" : "0" }} />
                        );
                      })}
                    </div>
                    <span className="text-[8px] text-slate-500">{yr.year}</span>
                  </div>
                );
              })}
            </div>
          </div>
          {/* Legend */}
          <div className="flex flex-wrap gap-3 mt-3 justify-center">
            {Object.entries(sourceColors).map(([key, s]) => {
              if (!data.sources?.[key]) return null;
              return (
                <div key={key} className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-sm" style={{ background: s.text }} />
                  <span className="text-[9px] text-slate-400">{s.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </Reveal>

      {/* Top Research Fields */}
      <Reveal delay={400}>
        <div className="p-4 rounded-lg border border-white/5 bg-white/[0.02]">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-4 h-4 text-purple-400" />
            <h3 className="text-sm font-semibold text-white">Top Research Fields</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {(data.top_fields || []).slice(0, 12).map((f, i) => (
              <div key={f.field || i} className="flex items-center gap-2">
                <span className="text-[10px] text-slate-400 w-40 truncate">{f.field}</span>
                <div className="flex-1">
                  <MiniBar value={f.count} max={maxField} color="#a855f7" />
                </div>
                <span className="text-[10px] text-slate-500 w-12 text-right font-mono">{f.count.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      {/* Recently Ingested Papers */}
      <Reveal delay={480}>
        <div className="p-4 rounded-lg border border-white/5 bg-white/[0.02]">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-amber-400" />
            <h3 className="text-sm font-semibold text-white">Recently Ingested</h3>
          </div>
          <div className="space-y-2">
            {(data.recent_papers || []).map((p, i) => {
              const s = sourceColors[p.source] || { text: "#94a3b8", label: p.source };
              return (
                <div key={p.paper_title || i} className="flex items-start gap-2 py-1.5 border-b border-white/5 last:border-0">
                  <Zap className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: s.text }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-slate-300 truncate">{p.paper_title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: sourceColors[p.source]?.bg, color: sourceColors[p.source]?.text }}>{s.label}</span>
                      {p.year && <span className="text-[9px] text-slate-500">{p.year}</span>}
                      {p.country && p.country !== "Unknown" && <span className="text-[9px] text-slate-500">{p.country}</span>}
                      {p.value_orientation && p.value_orientation !== "unclassified" && (
                        <span className={`text-[9px] px-1 rounded ${p.value_orientation === "care" ? "bg-emerald-500/20 text-emerald-400" : p.value_orientation === "extraction" ? "bg-red-500/20 text-red-400" : "bg-blue-500/20 text-blue-400"}`}>
                          {p.value_orientation}
                        </span>
                      )}
                    </div>
                  </div>
                  {p.citations > 0 && <span className="text-[9px] text-slate-500 flex-shrink-0">{p.citations} cites</span>}
                </div>
              );
            })}
          </div>
        </div>
      </Reveal>

      {/* Footer */}
      <Reveal delay={560}>
        <div className="text-center py-2">
          <p className="text-[9px] text-slate-600">
            Data pipeline refreshes every 5 minutes. Sources: arXiv, Semantic Scholar, HuggingFace, SSRN (via OpenAlex), Stanford HAI (via OpenAlex)
          </p>
        </div>
      </Reveal>
    </div>
  );
};
